import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AXIOS_INSTANCE } from '../../services/api-client';
import { useToasts } from '../../stores/useToasts';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Spinner,
} from '../../components/ui';

function getErrorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { detail?: unknown } } };
  const detail = candidate?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((entry) => {
        if (entry && typeof entry === 'object' && 'msg' in entry) {
          const msg = (entry as { msg?: unknown }).msg;
          return typeof msg === 'string' ? msg : JSON.stringify(entry);
        }
        return JSON.stringify(entry);
      })
      .join(' | ');
  }
  return fallback;
}

export default function ForgotPasswordPage() {
  const { push } = useToasts();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) {
      push({ kind: 'error', message: 'Please enter your email address' });
      return;
    }

    setIsSubmitting(true);
    try {
      await AXIOS_INSTANCE.post('/v1/auth/password-reset/request', {
        email: email.trim(),
      });
      setSubmitted(true);
    } catch (error) {
      push({ kind: 'error', message: getErrorMessage(error, 'Failed to request password reset') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Forgot your password?</CardTitle>
          <CardDescription>
            Enter the email associated with your Eliza Accelerate account and we'll send you a secure
            reset link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-4">
              <Alert variant="success">
                If an account exists for {email.trim()}, a password reset link has been sent. The link
                expires in 30 minutes.
              </Alert>
              <Link to="/login" className="text-sm text-eliza-red hover:underline">
                Back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? <Spinner size="sm" className="mr-2" /> : null}
                Send reset link
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-sm text-eliza-red hover:underline">
                  Back to login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
